/**
 * Token Refresh Hook
 * 
 * Feature: 033-auth-complete (Phase 4 - User Story 2)
 * Description: Proactively refreshes MSAL tokens before expiry
 * Constitution: v1.11.0
 */

import { useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAuthStore } from '../../stores/authStore';
import { apiTokenRequest } from '../config';
import axios from 'axios';

const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh when <5 minutes remaining
const TOKEN_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

export const useTokenRefresh = () => {
  const { instance, accounts } = useMsal();
  const { isAuthenticated, setError } = useAuthStore();
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Check token expiry and refresh if needed
   */
  const checkAndRefreshToken = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) return;

    try {
      const account = accounts[0];
      
      // Get current token (silent)
      const response = await instance.acquireTokenSilent({
        scopes: apiTokenRequest.scopes,
        account,
      });

      // Check if token is expiring soon
      const expiresOn = response.expiresOn;
      if (expiresOn) {
        const now = new Date();
        const timeUntilExpiry = expiresOn.getTime() - now.getTime();

        // Refresh if less than 5 minutes remaining
        if (timeUntilExpiry < TOKEN_REFRESH_THRESHOLD_MS) {
          console.log('Token expiring soon, refreshing...');
          
          // Force refresh
          const refreshResponse = await instance.acquireTokenSilent({
            scopes: apiTokenRequest.scopes,
            account,
            forceRefresh: true,
          });

          console.log('Token refreshed successfully');

          // Log token refresh to audit trail
          try {
            await axios.post('/api/auth/session/activity', { isRefresh: true }, {
              headers: {
                Authorization: `Bearer ${refreshResponse.idToken}`,
              },
            });
          } catch (auditError) {
            console.warn('Failed to log token refresh:', auditError);
            // Don't fail the refresh if audit logging fails
          }
        }
      }
    } catch (error: any) {
      console.error('Token refresh error:', error);

      // If silent refresh fails, try interactive
      if (error.errorCode === 'consent_required' || 
          error.errorCode === 'interaction_required' || 
          error.errorCode === 'login_required') {
        try {
          await instance.acquireTokenRedirect({
            scopes: apiTokenRequest.scopes,
          });
        } catch (redirectError) {
          console.error('Token redirect error:', redirectError);
          setError('Session expired. Please sign in again.');
        }
      }
    }
  }, [isAuthenticated, accounts, instance, setError]);

  /**
   * Setup token refresh interval
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check immediately on mount
    checkAndRefreshToken();

    // Check every minute
    tokenCheckIntervalRef.current = setInterval(() => {
      checkAndRefreshToken();
    }, TOKEN_CHECK_INTERVAL_MS);

    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
    };
  }, [isAuthenticated, checkAndRefreshToken]);

  return {
    checkAndRefreshToken,
  };
};
